import { useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";

import EprDao from "../dao/EprDao";
import Marquee from "./Marquee";
import SimpleDialog from "./dialogs/SimpleDialog";
import StudentApplications from "./StudentApplications";
import StudentCarousel from "./StudentCarousel";
import StudentInfoDao from "../dao/StudentInfoDao";
import T from "./translations/Translations";

import { GlobalContext } from "./contextProvider/ContextProvider";
import { isAllowed, isWindowOpen } from "../utils/ActionControlWindows";

import "../styles/StudentsContainer.scss";

/**
 * Display the student carousel, the selected student marquee and possibly welcome text
 * @name StudentsContainer
 * @param {bool} loader
 * @param {func} setLoader
 * @returns {JSX.Element}
 */
const StudentsContainer = ({ loader, setLoader }) => {
    const { dispatch, state } = useContext(GlobalContext);
    const {
        allLocationDtos,
        oeActionControls,
        oeActiveDistrictWindow,
        oeDistrictWindows,
        guardianState,
        lang,
        schoolYearDto,
        studentImageList,
        studentsHomeSchooled,
        studentsFlag,
        studentsIc,
        studentsOe,
        timePeriods,
        token
    } = state || {};
    const [addAllowed, setAddAllowed] = useState(false);
    const [expand, setExpand] = useState(false);
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [studentList, setStudentList] = useState(null);

    const globalNoStudent1 = T({ key: "Global_No_Student_1" });
    const globalNoStudent2 = T({ key: "Global_No_Student_2" });
    const globalNoStudentExternal = T({ key: "Global_No_Student_External" });
    const globalNoStudentGuardian = T({ key: "Global_No_Student_Guardian" });

    /**
     * Determine if the user is allowed to add a student
     */
    useEffect(() => {
        if (!oeActionControls || !oeActiveDistrictWindow || !oeDistrictWindows) {
            setAddAllowed(false);
            return;
        }
        if (
            isAllowed("PARENT_REQUEST", oeActionControls, oeActiveDistrictWindow, oeDistrictWindows) &&
            isWindowOpen(oeActiveDistrictWindow.startDate, oeActiveDistrictWindow.endDate)
        ) {
            setAddAllowed(true);
        } else {
            setAddAllowed(false);
        }
    }, [oeActionControls, oeActiveDistrictWindow, oeDistrictWindows]);

    /**
     * Get a list of IC student image blobs
     */
    useEffect(() => {
        if (!schoolYearDto || !studentsIc || !token) {
            return;
        }

        /**
         * @name fetchStudentImages
         * @description Get a list of IC student image blobs
         * @async
         */
        const fetchStudentImages = async () => {
            try {
                const imagePromises = studentsIc.map(async (student) => {
                    const options = {
                        action: "studentImageRead",
                        ignoreError: true,
                        schoolYearKey: schoolYearDto.key,
                        studentNumber: student.studentNumber,
                        token
                    };
                    try {
                        const response = await StudentInfoDao(options);
                        if (response && response.data) {
                            const urlCreator = window.URL || window.webkitURL;
                            return {
                                studentNumber: student.studentNumber,
                                imageUrl: urlCreator.createObjectURL(new Blob([response.data], { type: "image/jpeg" }))
                            };
                        }
                    } catch (error) {
                        console.error(`No image found for student ${student.studentNumber}:\n`, error);
                    }
                });

                // Resolve all promises and filter out null results
                const studentImages = (await Promise.all(imagePromises)).filter(Boolean);
                // Dispatch only after all images have been fetched
                dispatch({
                    type: "StudentImageList",
                    studentImageList: studentImages
                });
            } catch (error) {
                console.error(`Unable to retrieve Student Images for school year ${schoolYearDto.key}:\n`, error);
            }
        };

        if (guardianState && studentImageList?.length !== studentsIc.length) {
            fetchStudentImages();
        }
    }, [dispatch, guardianState, schoolYearDto, setLoader, studentImageList, studentsIc, token]);

    /**
     * Get a list of home schooled students
     */
    useEffect(() => {
        if (studentsHomeSchooled || !allLocationDtos || !studentsIc || !token) {
            return;
        }

        /**
         * @name fetchHomeSchooledStudents
         * @description Determine if a student is home schooled
         * @async
         */
        const fetchHomeSchooledStudents = async () => {
            try {
                const homeSchoolPromises = studentsIc.map(async (student) => {
                    let schoolId = student?.schoolId;
                    if (!schoolId && student.locationKey) {
                        const locMatch = allLocationDtos.filter(
                            (obj) => parseInt(obj.key, 10) === parseInt(student.locationKey, 10)
                        );
                        if (locMatch && locMatch.length > 0) {
                            schoolId = locMatch[0].icId[0];
                        }
                    }
                    const options = {
                        action: "studentInfoStudentRead",
                        personId: student.key,
                        schoolId,
                        token
                    };
                    try {
                        const response = await StudentInfoDao(options);
                        if (response && response.data) {
                            const { payload } = response.data;
                            return {
                                studentNumber: student.studentNumber,
                                isHomeSchooled: !["false", false].includes(payload)
                            };
                        }
                    } catch (error) {
                        console.error(
                            `Unable to retrieve home schooled status for student ${student.studentNumber}:\n`,
                            error
                        );
                    }
                });
                // Resolve all promises
                const studentsHome = await Promise.all(homeSchoolPromises);
                dispatch({
                    type: "StudentsHomeSchooled",
                    studentsHomeSchooled: studentsHome
                });
            } catch (error) {
                console.error(`Unable to retrieve Student Images for school year ${schoolYearDto.key}:\n`, error);
            }
        };

        fetchHomeSchooledStudents();
    }, [allLocationDtos, dispatch, schoolYearDto, setLoader, studentsHomeSchooled, studentsIc, token]);

    /**
     * Assemble a list of IC and OE students combined into a common dto
     */
    useEffect(() => {
        const validStudentsIc = studentsIc || [];
        const validStudentsOe = studentsOe || [];

        /**
         * @name getStudents
         * @description Assemble a list of IC and OE students combined into a common dto
         */
        const getStudents = () => {
            const oeFromIc = validStudentsIc.reduce((results, dto) => {
                const oeExists = studentsOe.filter(
                    (obj) => parseInt(obj.studentDto?.studentNumber, 10) === parseInt(dto.studentNumber, 10)
                );
                if (oeExists && oeExists.length > 0) {
                    oeExists[0].schoolName = dto.schoolName;
                    results.push(oeExists[0]);
                } else {
                    results.push(dto);
                }

                return results;
            }, []);

            const filteredOe = validStudentsOe.filter((obj) => obj.studentDto.studentNumber === null);
            const combined = [...oeFromIc, ...filteredOe];
            combined.sort((a, b) => {
                const nameA = a.studentDto ? a.studentDto.lastName : a.lastName;
                const nameB = b.studentDto ? b.studentDto.lastName : b.lastName;

                return nameA.localeCompare(nameB);
            });
            const formatted = combined.reduce((results, obj) => {
                const { schoolChoiceDetailDtos, schoolId, schoolName, studentDto } = obj;
                const dto = studentDto ? studentDto : obj;
                dto.schoolChoiceDetailDtos = schoolChoiceDetailDtos ? schoolChoiceDetailDtos : [];
                dto.schoolName = schoolName ? schoolName : null;
                dto.studentKey = studentDto ? studentDto.key : null;
                dto.schoolId = schoolId ? schoolId : null;
                results.push(dto);

                return results;
            }, []);
            setStudentList(formatted);
            if (formatted.length > 0) {
                setSelectedStudent(formatted[0]);
            }
        };

        // This check ensures the effect only runs when you have data to process.
        if (validStudentsIc.length > 0 || validStudentsOe.length > 0) {
            getStudents();
        }
    }, [studentsIc, studentsOe]);

    /**
     * Get a list of time periods for the school year
     */
    useEffect(() => {
        if (timePeriods || !guardianState || !schoolYearDto || !token) {
            return;
        }

        /**
         * @name fetchTimePeriods
         * @description Get a list of all time periods for the school year
         * @async
         */
        const fetchTimePeriods = async () => {
            try {
                const options = {
                    action: "timePeriodsBySchoolYearRead",
                    schoolYearKey: schoolYearDto.key,
                    token
                };
                const response = await EprDao(options);
                if (response && response.data) {
                    const { payload } = response.data;
                    dispatch({
                        type: "TimePeriods",
                        timePeriods: payload
                    });
                }
            } catch (error) {
                console.error("Unable to retrieve time periods:\n", error);
            }
        };

        fetchTimePeriods();
    }, [dispatch, guardianState, schoolYearDto, timePeriods, token]);

    /**
     * Set loader to false after 10 seconds if the user has no students
     */
    useEffect(() => {
        if (studentsFlag?.OE && studentsFlag?.IC && !selectedStudent) {
            setLoader(false);
        }
    }, [selectedStudent, setLoader, studentsFlag]);

    return (
        <div className="students-container">
            <div className="carousel-container">
                <StudentCarousel
                    addAllowed={addAllowed}
                    setExpand={setExpand}
                    setSelectedStudent={setSelectedStudent}
                    studentImageList={studentImageList || []}
                    studentList={studentList || []}
                />
            </div>
            <hr />
            {selectedStudent && (
                <div className="student-info animate-height-from-fixed">
                    <div className="marquee">
                        {selectedStudent && <Marquee student={selectedStudent} studentImageList={studentImageList} />}
                    </div>
                    <div className="student-applications">
                        <StudentApplications
                            expand={expand}
                            isGuardian={guardianState}
                            lang={lang}
                            loader={loader}
                            setExpand={setExpand}
                            setLoader={setLoader}
                            student={selectedStudent}
                        />
                    </div>
                </div>
            )}
            {!loader && !selectedStudent && (
                <SimpleDialog
                    message1={globalNoStudent1}
                    message2={globalNoStudent2}
                    message3={guardianState ? globalNoStudentGuardian : globalNoStudentExternal}
                />
            )}
        </div>
    );
};

StudentsContainer.propTypes = {
    loader: PropTypes.bool,
    setLoader: PropTypes.func.isRequired
};

StudentsContainer.defaultProps = {
    loader: true
};

export default StudentsContainer;
